import { useEffect, useRef, useState } from "react";
import { temperature } from "@/lib/score";
import { Flame, Thermometer, Snowflake } from "lucide-react";

// Animated radial conversion-probability gauge (0-100) with temperature badge.
export default function ScoreGauge({ score = 0, size = 150 }) {
  const [shown, setShown] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = Math.max(0, Math.min(100, score || 0));
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  const stroke = 11;
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const col = shown >= 66 ? "#10b981" : shown >= 33 ? "#f59e0b" : "#ef4444";
  const offset = circ - (shown / 100) * circ;
  const t = temperature(score);
  const Icon = t?.key === "chaud" ? Flame : t?.key === "tiede" ? Thermometer : Snowflake;
  const hot = shown >= 80;

  return (
    <div className="relative flex flex-col items-center shrink-0" data-testid="score-gauge" data-score={shown}>
      <div className={hot ? "klt-score-glow" : ""} style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90 block">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" className="text-border" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 90ms linear, stroke 0.35s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-4xl font-extrabold tabular-nums leading-none" style={{ color: col }}>{shown}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">sur 100</span>
        </div>
      </div>
      {t && (
        <span className={`-mt-1 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${t.cls}`}>
          <Icon className="w-3.5 h-3.5" /> {t.label}
        </span>
      )}
    </div>
  );
}
